import {
  AutomatedParameter,
  AutomatedParameterInput,
  GasProductionDataType,
  TargetPowerScheduleConstraintType,
} from '@e2m-customer-portal/domains';
import { useLocale } from '@e2m-customer-portal/hooks';
import { Nullable } from '@e2m-customer-portal/shared';
import { NumberInput, ValidatedContainer } from '@e2m-customer-portal/ui';
import { setDay } from 'date-fns';
import { Dispatch, memo, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const pythonWeekday = (day: number) => (day + 1) % 7;

interface TargetPowerScheduleConstraintProps {
  data: TargetPowerScheduleConstraintType;
  updatedData: Nullable<GasProductionDataType>;
  targetPowerScheduleConstraints: TargetPowerScheduleConstraintType[];
  setTargetPowerScheduleConstraints: Dispatch<SetStateAction<TargetPowerScheduleConstraintType[] | undefined>>;
  readOnly: (parameter?: string) => boolean;
  setIsValid: Dispatch<SetStateAction<boolean>>;
}

function TargetPowerScheduleConstraintMemo({
  data,
  updatedData,
  targetPowerScheduleConstraints,
  setTargetPowerScheduleConstraints,
  readOnly,
  setIsValid,
}: Readonly<TargetPowerScheduleConstraintProps>) {
  const [t] = useTranslation();
  const { code: locale } = useLocale();

  const [updatedTargetPowerConstraint, setUpdatedTargetPowerConstraint] =
    useState<TargetPowerScheduleConstraintType>(data);

  useEffect(() => {
    const target_power_schedule_constraints = [...targetPowerScheduleConstraints].sort((a, b) => a.weekday - b.weekday);
    target_power_schedule_constraints[updatedTargetPowerConstraint.weekday] = updatedTargetPowerConstraint;
    setTargetPowerScheduleConstraints(target_power_schedule_constraints);
  }, [updatedTargetPowerConstraint]);

  useEffect(() => {
    setUpdatedTargetPowerConstraint(data);
  }, [data]);

  return (
    <div className="relative px-4 py-6 grid grid-cols-1 xl:grid-cols-2 gap-x-2 gap-y-4 bg-white rounded-md border-solid border border-divider">
      <ValidatedContainer validate="on" onValidated={setIsValid}>
        {updatedTargetPowerConstraint.power !== undefined && (
          <>
            <h5 className="m-0 font-semibold text-sm">
              {setDay(new Date(), pythonWeekday(updatedTargetPowerConstraint.weekday)).toLocaleDateString(locale, {
                weekday: 'long',
              })}
            </h5>
            {updatedTargetPowerConstraint.power.live_value != null && (
              <div className="font-medium text-sm text-right">
                {t('COPT_GAS_PRODUCTION_TARGET_POWER_SCHEDULE_CONSTRAINTS_POWER_LIVE_VALUE', {
                  value: updatedTargetPowerConstraint.power.live_value * 1000,
                })}
              </div>
            )}
            <AutomatedParameterInput
              parameter={updatedTargetPowerConstraint.power}
              className="w-full bg-white"
              name="power"
              label={t('COPT_GAS_PRODUCTION_TARGET_POWER_SCHEDULE_CONSTRAINTS_POWER')}
              liveParameterLabel="Prognose verwenden"
              disabled={readOnly('target_power_schedule_constraints')}
              notNegative
              minValue={Math.round((updatedData?.target_power_electric ?? 0) * 250)}
              maxValue={Math.round((updatedData?.target_power_electric ?? 0) * 2000)}
              valueFactor={1000}
              onParameterUpdate={(power: AutomatedParameter<number>) => {
                setUpdatedTargetPowerConstraint({
                  ...updatedTargetPowerConstraint,
                  power,
                });
              }}
            />
            <NumberInput
              value={
                updatedTargetPowerConstraint.power.default_value === null || updatedData?.target_power_electric == null
                  ? null
                  : Math.round(
                      (updatedTargetPowerConstraint.power.default_value / updatedData.target_power_electric) * 100,
                    )
              }
              className="w-full bg-white"
              name="power_percent"
              label={t('COPT_GAS_PRODUCTION_TARGET_POWER_SCHEDULE_CONSTRAINTS_PERCENT')}
              onChange={(value: Nullable<number>) => {
                setUpdatedTargetPowerConstraint({
                  ...updatedTargetPowerConstraint,
                  power: {
                    ...updatedTargetPowerConstraint.power,
                    default_value:
                      value != null && !Number.isNaN(value)
                        ? (Math.round(value) * (updatedData?.target_power_electric ?? 0)) / 100
                        : null,
                  },
                });
              }}
              disabled={readOnly('target_power_schedule_constraints')}
              notNegative
              minValue={25}
              maxValue={200}
            />
          </>
        )}
      </ValidatedContainer>
    </div>
  );
}

export const TargetPowerScheduleConstraint = memo(TargetPowerScheduleConstraintMemo);
